import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { userApi, subscriptionApi, teamApi, fairLendingApi, adminApi } from '../lib/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';
import { useTeamId } from '../hooks/useTeamId';
import { ExclamationTriangleIcon, ChatBubbleLeftRightIcon, ShieldCheckIcon, ArrowUpCircleIcon, BuildingLibraryIcon, MapPinIcon } from '@heroicons/react/24/outline';
import InstitutionSetupWizard from '../components/InstitutionSetupWizard';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuthStore();

  // Check if user is a super admin
  const isSuperAdmin = user?.isSuperAdmin || false;
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');
  const [institutionType, setInstitutionType] = useState(user?.institutionType || 'BANK');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [showSetupWizard, setShowSetupWizard] = useState(false);

  // Platform-data ingests (super-admin only).
  const [censusYear, setCensusYear] = useState(String(new Date().getFullYear() - 1));
  const [ingestResult, setIngestResult] = useState<string | null>(null);

  const crosswalkMutation = useMutation({
    mutationFn: () => adminApi.ingestZctaCrosswalk(),
    onSuccess: (r) => {
      const d: any = r.data;
      setIngestResult(`ZIP↔tract crosswalk: inserted ${Number(d.inserted ?? 0).toLocaleString()} rows (vintage ${d.vintage}).`);
      toast.success('Crosswalk loaded');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Crosswalk ingest failed'),
  });

  const censusMutation = useMutation({
    mutationFn: (year: number) => adminApi.ingestFfiecCensus(year),
    onSuccess: (r) => {
      const d: any = r.data;
      setIngestResult(`FFIEC census ${d.year}: ${Number(d.upserted ?? 0).toLocaleString()} tracts upserted (${d.total ?? 0} parsed, ${d.errors ?? 0} errors).`);
      toast.success('Census ingest complete');
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Census ingest failed'),
  });

  // Get user's active team via the shared useTeamId hook. Using a
  // page-local useQuery({ queryKey: ['teams'] }) would collide with
  // ~19 other pages that use the same key with different cache shapes —
  // see useTeamId.ts comment for the QA #5 root cause.
  const { teamId, isLoading: teamsLoading } = useTeamId();

  // Fetch institution setup status
  const { data: setupData } = useQuery({
    queryKey: ['institutionSetup', teamId],
    queryFn: () => teamApi.getInstitutionSetup(teamId!).then(r => r.data),
    enabled: !!teamId,
  });

  // Branch count for the Institution Setup card
  const { data: branchData } = useQuery({
    queryKey: ['branches', teamId],
    queryFn: () => fairLendingApi.getBranches(teamId!).then(r => r.data),
    enabled: !!teamId,
  });
  const branchCount = branchData?.branches?.length ?? 0;

  // Fetch plan info
  const { data: planInfo } = useQuery({
    queryKey: ['subscription-plan'],
    queryFn: async () => {
      const response = await subscriptionApi.getPlan();
      return response.data;
    },
    enabled: !!user,
  });

  const updateMutation = useMutation({
    mutationFn: (data: { firstName?: string; lastName?: string; institutionType?: string }) => userApi.updateProfile(data),
    onSuccess: (response) => {
      toast.success('Profile updated!');
      setUser(response.data);
    },
    onError: () => {
      toast.error('Failed to update profile. Please try again.');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate({ firstName, lastName, institutionType });
  };

  const deleteMutation = useMutation({
    mutationFn: (data: { password: string; confirmation: string }) =>
      userApi.deleteAccount(data),
    onSuccess: () => {
      toast.success('Account deleted successfully');
      localStorage.removeItem('token');
      navigate('/');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete account');
    },
  });

  const handleDeleteAccount = () => {
    if (deleteConfirmation !== 'DELETE MY ACCOUNT') {
      toast.error('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }

    if (!deletePassword && user?.passwordHash) {
      toast.error('Please enter your password');
      return;
    }

    deleteMutation.mutate({ password: deletePassword, confirmation: deleteConfirmation });
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
      </div>

      <div className="space-y-6">
        {/* Profile */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Profile</h2>
          </div>
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                value={user?.email}
                disabled
                className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-50"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Default Institution Type</label>
              <select
                value={institutionType}
                onChange={(e) => setInstitutionType(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="BANK">Bank (FDIC compliance)</option>
                <option value="CREDIT_UNION">Credit Union (NCUA compliance)</option>
                <option value="BOTH">Both / Agency (FDIC + NCUA compliance)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Account-level fallback only. Each bank&rsquo;s actual type now comes from its
                primary regulator, set in <span className="font-medium">Institution Setup</span> on the
                bank&rsquo;s page — that takes precedence over this default for compliance checks.
              </p>
            </div>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="bg-ocean-600 text-white px-4 py-2 rounded-md hover:bg-ocean-700 disabled:opacity-50 transition-all shadow-sm hover:shadow-md"
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>

        {/* Institution Setup */}
        {(teamId || teamsLoading) && (
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Institution Setup</h2>
            </div>
            <div className="p-6">
              {teamsLoading ? (
                <p className="text-sm text-gray-500">Loading...</p>
              ) : (
                <div className="flex items-start space-x-3">
                  <BuildingLibraryIcon className="h-6 w-6 text-ocean-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    {setupData?.setupCompleted ? (
                      <>
                        <h3 className="text-sm font-medium text-gray-900">Institution Configured</h3>
                        <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                          <div><span className="text-gray-500">Name:</span> <span className="font-medium">{setupData.institutionName}</span></div>
                          <div><span className="text-gray-500">Regulator:</span> <span className="font-medium">{setupData.primaryRegulator}</span></div>
                          <div><span className="text-gray-500">Charter:</span> <span className="font-medium">{setupData.charterType?.replace(/_/g, ' ')}</span></div>
                          <div><span className="text-gray-500">State:</span> <span className="font-medium">{setupData.state}</span></div>
                          {setupData.fdicCertNumber && (
                            <div><span className="text-gray-500">FDIC Cert:</span> <span className="font-medium">{setupData.fdicCertNumber}</span></div>
                          )}
                          {setupData.assetSizeBucket && (
                            <div><span className="text-gray-500">CRA Asset Size:</span> <span className="font-medium">{({SMALL: '<$412 million', INTERMEDIATE: '$412 million to <$1.649 billion', LARGE: '$1.649 billion +'} as Record<string, string>)[setupData.assetSizeBucket] || setupData.assetSizeBucket}</span></div>
                          )}
                        </div>
                        <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
                          <div className="flex items-center gap-2 text-sm">
                            <MapPinIcon className="h-4 w-4 text-gray-400" />
                            <span className="text-gray-500">Branches:</span>
                            <span className="font-medium">{branchCount}</span>
                          </div>
                          <Link to="/branches" className="text-sm font-medium text-ocean-600 hover:text-ocean-700">
                            Manage Branches →
                          </Link>
                        </div>
                        <button
                          onClick={() => setShowSetupWizard(true)}
                          className="mt-4 border border-ocean-600 text-ocean-600 px-4 py-2 rounded-md hover:bg-ocean-50 transition-all text-sm font-medium"
                        >
                          Update Institution Setup
                        </button>
                      </>
                    ) : (
                      <>
                        <h3 className="text-sm font-medium text-gray-900">Not yet configured</h3>
                        <p className="text-sm text-gray-600 mt-1">
                          Set up your institution profile to enable Peer Benchmarking, Financial Metrics, and other modules that need institution-specific configuration.
                        </p>
                        <button
                          onClick={() => setShowSetupWizard(true)}
                          className="mt-4 bg-ocean-600 text-white px-4 py-2 rounded-md hover:bg-ocean-700 transition-all shadow-sm hover:shadow-md text-sm font-medium"
                        >
                          Run Institution Setup
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Subscription & Plan */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Subscription & Plan</h2>
          </div>
          <div className="p-6">
            {/* Current Plan */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-medium text-gray-700">Current Plan</h3>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {(planInfo?.plan || user?.subscriptionPlan || 'PROFESSIONAL') === 'ENTERPRISE' ? 'Enterprise' : 'Professional'}
                  </p>
                </div>
                {planInfo?.plan !== 'ENTERPRISE' && (
                  <button
                    onClick={() => navigate('/contact')}
                    className="flex items-center gap-2 text-sm font-medium text-ocean-600 hover:text-ocean-700"
                  >
                    <ArrowUpCircleIcon className="h-5 w-5" />
                    Contact Sales
                  </button>
                )}
              </div>

              {/* Plan Limits */}
              {planInfo && (
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600">Monthly Scans</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {planInfo.usage.scans} / {planInfo.limits.maxScans || '∞'}
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-xs text-gray-600">Team Members</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {planInfo.usage.teamMembers} / {planInfo.limits.maxUsers || '∞'}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Billing Status */}
            <div className="mb-4 pb-4 border-b border-gray-200">
              <p className="text-sm text-gray-600">
                Status: <span className="font-semibold">{
                  user?.subscriptionStatus === 'ACTIVE' ? 'Active' :
                  user?.subscriptionStatus === 'TRIAL' ? 'Trial' :
                  user?.subscriptionStatus === 'PAST_DUE' ? 'Past Due' :
                  user?.subscriptionStatus === 'CANCELED' ? 'Canceled' :
                  user?.subscriptionStatus || 'Unknown'
                }</span>
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/billing')}
                className="bg-ocean-600 text-white px-4 py-2 rounded-md hover:bg-ocean-700 transition-all shadow-sm hover:shadow-md"
              >
                Manage Billing
              </button>
              <button
                onClick={() => navigate('/contact')}
                className="border border-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-50 transition-all"
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>

        {/* Admin Panel (only for super admins) */}
        {isSuperAdmin && (
          <div className="bg-white shadow rounded-lg border-2 border-ocean-200">
            <div className="px-6 py-4 border-b border-ocean-200 bg-ocean-50">
              <h2 className="text-lg font-semibold text-ocean-900">Administration</h2>
            </div>
            <div className="p-6">
              <div className="flex items-start space-x-3">
                <ShieldCheckIcon className="h-6 w-6 text-ocean-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-gray-900">Compliance Administration</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Manage compliance rule versions and monitor regulatory updates from FDIC and NCUA.
                  </p>
                  <button
                    onClick={() => navigate('/admin')}
                    className="mt-4 bg-ocean-600 text-white px-4 py-2 rounded-md hover:bg-ocean-700 transition-all shadow-sm hover:shadow-md text-sm font-medium"
                  >
                    Go to Admin Panel
                  </button>
                </div>
              </div>

              {/* Platform reference-data ingests (global — affects all tenants) */}
              <div className="mt-6 pt-6 border-t border-ocean-100">
                <div className="flex items-start space-x-3">
                  <MapPinIcon className="h-6 w-6 text-ocean-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-gray-900">Platform Reference Data</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Global lookup data used across all tenants. Run these once after setup or when a new
                      vintage publishes — they download large public files and take 1–2 minutes.
                    </p>

                    <div className="mt-4 space-y-4">
                      {/* ZIP↔tract crosswalk */}
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          onClick={() => crosswalkMutation.mutate()}
                          disabled={crosswalkMutation.isPending}
                          className="bg-ocean-600 text-white px-4 py-2 rounded-md hover:bg-ocean-700 disabled:opacity-50 text-sm font-medium"
                        >
                          {crosswalkMutation.isPending ? 'Loading…' : 'Load ZIP↔Tract Crosswalk'}
                        </button>
                        <span className="text-xs text-gray-500">Census 2020 ZCTA→tract — powers the marketing distribution overlay.</span>
                      </div>

                      {/* FFIEC census re-ingest */}
                      <div className="flex flex-wrap items-center gap-3">
                        <button
                          onClick={() => censusMutation.mutate(Number(censusYear))}
                          disabled={censusMutation.isPending || !censusYear}
                          className="bg-ocean-600 text-white px-4 py-2 rounded-md hover:bg-ocean-700 disabled:opacity-50 text-sm font-medium"
                        >
                          {censusMutation.isPending ? 'Ingesting…' : 'Re-ingest FFIEC Census'}
                        </button>
                        <input
                          type="number"
                          min={2018}
                          max={new Date().getFullYear()}
                          value={censusYear}
                          onChange={(e) => setCensusYear(e.target.value)}
                          className="w-24 rounded-md border border-gray-300 px-3 py-2 text-sm"
                          aria-label="Census year"
                        />
                        <span className="text-xs text-gray-500">Populates tract income tiers + OOHU for the Lending Test.</span>
                      </div>

                      {ingestResult && (
                        <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-md px-3 py-2">
                          {ingestResult}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Support */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Support</h2>
          </div>
          <div className="p-6">
            <div className="flex items-start space-x-3">
              <ChatBubbleLeftRightIcon className="h-6 w-6 text-ocean-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900">Need Help?</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Have questions or need assistance? Our support team is here to help you with any issues or questions about Clarid.
                </p>
                <Link
                  to="/contact"
                  className="inline-block mt-4 bg-ocean-600 text-white px-4 py-2 rounded-md hover:bg-ocean-700 transition-all shadow-sm hover:shadow-md text-sm font-medium"
                >
                  Contact Support
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Danger Zone */}
        <div className="bg-white shadow rounded-lg border-2 border-red-200">
          <div className="px-6 py-4 border-b border-red-200 bg-red-50">
            <h2 className="text-lg font-semibold text-red-900">Danger Zone</h2>
          </div>
          <div className="p-6">
            <div className="flex items-start space-x-3">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900">Delete Account</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Once you delete your account, there is no going back. This will permanently delete your account, all your reports, and cancel your subscription.
                </p>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="mt-4 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-all shadow-sm hover:shadow-md text-sm font-medium"
                >
                  Delete My Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Institution Setup Wizard */}
      {teamId && (
        <InstitutionSetupWizard
          isOpen={showSetupWizard}
          onClose={() => setShowSetupWizard(false)}
          teamId={teamId}
        />
      )}

      {/* Delete Account Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
              <h3 className="text-xl font-bold text-gray-900">Delete Account</h3>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              This action cannot be undone. This will permanently delete your account and all associated data.
            </p>

            <div className="space-y-4">
              {user?.passwordHash && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Enter your password
                  </label>
                  <input
                    type="password"
                    value={deletePassword}
                    onChange={(e) => setDeletePassword(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    placeholder="Your password"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type <span className="font-mono font-bold">DELETE MY ACCOUNT</span> to confirm
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  placeholder="DELETE MY ACCOUNT"
                />
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletePassword('');
                  setDeleteConfirmation('');
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-all"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteMutation.isPending}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete Account'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
